// ─── Jetons d'éléments gérables (Core, pur) ───────────────────────
// Le forfait ne limite pas que les FONCTIONS : il limite aussi le nombre
// d'ÉLÉMENTS (calendrier, inventaire, plan, budget…) qu'un utilisateur peut
// réellement utiliser. Chaque élément utilisé consomme un JETON.
//
//   ⚙️ gestion      → voir ET modifier
//   👁 consultation → voir seulement
// Les DEUX peuvent être offerts : offrir la lecture coûte un jeton 👁, bien
// moins rare qu'un ⚙️ — c'est le partage le plus fréquent (« regarde mon
// planning » ne demande pas de pouvoir le modifier).
//
// Règles (décidées avec Cédric) :
//   • tout type d'élément compte dans le même compteur, Y COMPRIS ceux dont on
//     est propriétaire ;
//   • PARTAGER ne coûte rien : donner accès à un élément est gratuit, seul son
//     USAGE consomme un jeton, chez chacun. Le DON sert à offrir cet usage à
//     quelqu'un qui n'a plus de jeton libre : il reste alors immobilisé chez le
//     donneur, dans la nature offerte ;
//   • un élément non activé est listé mais ni consultable ni modifiable ;
//   • la sélection ne change qu'UNE fois par mois calendaire — sauf reprendre
//     un don (libre) et promouvoir 👁 → ⚙️ (libre).
//
// Aucune dépendance Firestore/UI : testable et réutilisable.

export const JETON_GESTION = 'gestion';
export const JETON_VUE = 'vue';

// Dotation par défaut si `config/fonctionnalites.plans[plan]` ne dit rien.
export const JETONS_DEFAUT = { gestion: 2, vue: 1 };

// Nombre d'ESPACES qu'un compte peut posseder. Un espace n'est pas un element
// (il n'occupe pas de jeton) : c'est le CONTENANT, et il se compte a part.
// 1 par defaut pour TOUS les plans — l'administration releve ce chiffre plan
// par plan quand l'offre le justifiera (cf. `espacesDuPlan`).
export const ESPACES_DEFAUT = 1;

// ── Dotation ──────────────────────────────────────────────────────
// Priorité : surcharge IVV (si activée) → réglage du plan → défaut.
export function jetonsDuPlan(cfg, plan) {
  const j = cfg?.plans?.[plan]?.jetons;
  return {
    gestion: Number.isFinite(j?.gestion) ? j.gestion : JETONS_DEFAUT.gestion,
    vue: Number.isFinite(j?.vue) ? j.vue : JETONS_DEFAUT.vue,
  };
}
// Espaces autorises pour un plan. Meme grammaire que les jetons : reglage en
// base (config/fonctionnalites.plans[plan].maxEspaces), repli sur le defaut.
export function espacesDuPlan(cfg, plan) {
  const n = cfg?.plans?.[plan]?.maxEspaces;
  return Number.isFinite(n) ? n : ESPACES_DEFAUT;
}

// Dotation en ESPACES, surcharge IVV comprise (meme bascule que les jetons :
// un valideur regle ses quotas pour tester, sans toucher a la grille des plans).
export function dotationEspaces(cfg, user) {
  const base = espacesDuPlan(cfg, user?.plan || 'gratuit');
  const d = (user?.ivvSurchargeJetons && Number.isFinite(user?.maxEspaces)) ? user.maxEspaces : base;
  return d + bonusAchete(user, 'espaces');
}

// ── Plafonds PAR TYPE d'élément (18/08) ─────────────────────────────
// Distinct du pool jetons gestion/vue (qui compte TOUS les types ensemble) :
// combien de calendriers, d'inventaires/plans, de menus (My Meals) ou de
// comptes/budgets (My Finance) un plan autorise, chacun à part. Réglage en
// base (config/fonctionnalites.plans[plan].maxParType.{type}), même grammaire
// que `maxEspaces`. `null`/absent = ILLIMITÉ (pas de plafond posé encore) :
// aucun module n'applique ces plafonds à ce jour, ils les liront quand ils
// construiront leur propre garde de quota — un défaut restrictif casserait
// silencieusement des comptes existants avant qu'un module ne sache pourquoi.
export const TYPES_ELEMENTS = ['calendriers', 'inventaires', 'repas', 'comptes'];
export function maxTypeDuPlan(cfg, plan, type) {
  const n = cfg?.plans?.[plan]?.maxParType?.[type];
  return Number.isFinite(n) ? n : null;
}

// Plafond EFFECTIF (plan + bonus acheté à la carte, cf. plus bas). `null`
// (illimité) reste illimité — un bonus n'a aucun sens à ajouter à l'infini.
export function maxTypeEffectif(cfg, user, type) {
  const base = maxTypeDuPlan(cfg, user?.plan || 'gratuit', type);
  return base == null ? null : base + bonusAchete(user, type);
}

// ── Bonus ACHETÉ à la carte (19/08) ─────────────────────────────────
// `users.quotasAchetes = { [cle]: n }` — un bonus ADDITIF, distinct par
// nature (cf. TYPES_ELEMENTS ci-dessous, plus 'gestion'/'vue'/'espaces').
// Octroyé par l'administration SEULEMENT (firestore.rules verrouille ce
// champ comme `fonctionnalitesAchetees`) : jamais écrit par le client.
export function bonusAchete(user, cle) {
  const n = user?.quotasAchetes?.[cle];
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function dotation(cfg, user) {
  const base = jetonsDuPlan(cfg, user?.plan || 'gratuit');
  // Même logique que les fonctions : la surcharge IVV n'est lue que si la
  // bascule du profil est armée (sinon un ancien champ ferait loi).
  const d = !user?.ivvSurchargeJetons ? base : (() => {
    const j = user?.jetons || {};
    return {
      gestion: Number.isFinite(j.gestion) ? j.gestion : base.gestion,
      vue: Number.isFinite(j.vue) ? j.vue : base.vue,
    };
  })();
  // Le bonus acheté s'ajoute TOUJOURS, surcharge IVV ou pas : c'est un achat
  // réel, pas une simulation de test.
  return {
    gestion: d.gestion + bonusAchete(user, 'gestion'),
    vue: d.vue + bonusAchete(user, 'vue'),
  };
}

// ── Consommation ──────────────────────────────────────────────────
// `affectations` : { [cleElement]: 'gestion' | 'vue' } — ce que l'utilisateur
// a activé pour lui-même. `dons` : [{ cle, uid }] — jetons ⚙️ qu'il a donnés
// (immobilisés chez lui, quel que soit l'usage qu'en fait le bénéficiaire).
// Un don porte sa NATURE : offrir la lecture coûte un jeton 👁, pas un ⚙️.
// Sans `nature` (dons créés avant ce choix) = gestion.
export const natureDon = (d) => (d && d.nature === JETON_VUE ? JETON_VUE : JETON_GESTION);
export function consommation(affectations = {}, dons = []) {
  const c = { gestion: 0, vue: 0 };
  for (const nature of Object.values(affectations || {})) {
    if (nature === JETON_GESTION) c.gestion += 1;
    else if (nature === JETON_VUE) c.vue += 1;
  }
  for (const d of (dons || [])) c[natureDon(d)] += 1;
  return c;
}
export function restants(cfg, user, affectations, dons) {
  const d = dotation(cfg, user);
  const c = consommation(affectations, dons);
  return { gestion: d.gestion - c.gestion, vue: d.vue - c.vue };
}

// ── Droit sur un élément ──────────────────────────────────────────
// Renvoie 'gestion' | 'vue' | null. Un jeton REÇU (don) prime : il ne coûte
// rien au bénéficiaire et vaut toujours gestion.
export function droitElement(cle, { affectations = {}, recus = [] } = {}) {
  const recu = (recus || []).find(r => r.cle === cle);
  if (recu) return natureDon(recu);              // un don reçu vaut CE qu'on a offert
  return affectations?.[cle] || null;
}
export const peutModifierElement = (cle, etat) => droitElement(cle, etat) === JETON_GESTION;
export const peutVoirElement = (cle, etat) => !!droitElement(cle, etat);

// ── Allocation par PRIORITÉ ───────────────────────────────────────
// La sélection de l'utilisateur est une liste ORDONNÉE : l'index est la
// priorité. On distribue les jetons en descendant cette liste, ce qui rend la
// perte de jetons (don repris, forfait rétrogradé) prévisible — ce qui compte
// le plus reste accessible, le bas de liste est écarté SANS RIEN PERDRE : il
// revient tel quel dès qu'un jeton se libère.
//
//   selection : FILE UNIQUE ordonnée [{ cle, nature, pour? }] — mon usage d'un
//               élément et l'offre que j'en fais sont deux ENTRÉES distinctes :
//               chacune coûte un jeton, et se priorise indépendamment de l'autre.
//   recus     : [{ cle, nature }] — jetons reçus (hors quota, servis d'abord)
//
// Une entrée ⚙️ dont le jeton manque prend un 👁 s'il en reste : mieux vaut voir
// que ne rien voir. L'appelant l'affiche comme « rétrogradé ».
export const idEntree = (x) => (x?.pour ? `${x.cle}@${x.pour}` : x?.cle);
export function allouer(cfg, user, selection = [], recus = []) {
  const d = dotation(cfg, user);
  let libreG = d.gestion, libreV = d.vue;
  const actifs = {}, retrogrades = [], ecartes = [];

  for (const r of (recus || [])) actifs[r.cle] = natureDon(r);    // gratuits, dans la nature offerte

  for (const item of (selection || [])) {
    const id = idEntree(item);
    if (!id) continue;
    // Un élément déjà servi par un don REÇU n'a pas à consommer mes jetons —
    // sauf s'il s'agit d'une offre (une autre entrée, un autre jeton).
    if (!item.pour && actifs[item.cle] && !retrogrades.includes(item.cle)) continue;
    if (item.nature === JETON_GESTION) {
      if (libreG > 0) { actifs[id] = JETON_GESTION; libreG -= 1; }
      else if (libreV > 0) { actifs[id] = JETON_VUE; libreV -= 1; retrogrades.push(id); }
      else ecartes.push(id);
    } else {
      if (libreV > 0) { actifs[id] = JETON_VUE; libreV -= 1; }
      else ecartes.push(id);
    }
  }
  return { actifs, retrogrades, ecartes, restants: { gestion: Math.max(0, libreG), vue: Math.max(0, libreV) } };
}

// Déplace une ENTRÉE dans l'ordre de priorité (id = cle, ou cle@beneficiaire).
export function deplacerPriorite(selection = [], id, versIndex) {
  const l = [...selection];
  const de = l.findIndex(x => idEntree(x) === id);
  if (de < 0) return l;
  const cible = Math.max(0, Math.min(l.length - 1, versIndex));
  l.splice(cible, 0, ...l.splice(de, 1));
  return l;
}

// ── Dépassement et délai de grâce ─────────────────────────────────
// On peut se retrouver AU-DESSUS de sa dotation sans l'avoir voulu : un don
// repris, une baisse de forfait, un partage de plus. On ne coupe alors rien
// brutalement — l'utilisateur dispose de GRACE_JOURS pour choisir ce qu'il
// garde, avec un avertissement à la connexion. Passé ce délai, les éléments en
// trop deviennent inaccessibles (jamais supprimés : libérer un jeton suffit à
// les retrouver).
export const GRACE_JOURS = 7;

// Nombre d'éléments EN TROP, par nature (0 si tout va bien).
export function depassement(cfg, user, affectations, dons) {
  const r = restants(cfg, user, affectations, dons);
  return { gestion: Math.max(0, -r.gestion), vue: Math.max(0, -r.vue) };
}
export function estEnDepassement(cfg, user, affectations, dons) {
  const d = depassement(cfg, user, affectations, dons);
  return d.gestion > 0 || d.vue > 0;
}
// Fin du délai, en « AAAA-MM-JJ ». `depuis` = users.jetonsDepassementDepuis,
// posé le jour où le dépassement est constaté.
export function finDeGrace(depuis) {
  if (!depuis) return null;
  const d = new Date(String(depuis).slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + GRACE_JOURS);
  return d.toISOString().slice(0, 10);
}
// `aujourdhui` est passé par l'appelant (« AAAA-MM-JJ ») : le moteur reste pur.
export function graceExpiree(depuis, aujourdhui) {
  const fin = finDeGrace(depuis);
  return !!(fin && aujourdhui && aujourdhui > fin);
}
export function joursDeGraceRestants(depuis, aujourdhui) {
  const fin = finDeGrace(depuis);
  if (!fin || !aujourdhui) return null;
  const ms = new Date(fin).getTime() - new Date(aujourdhui).getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

// Le délai court PAR ÉLÉMENT : chacun perd son jeton à un moment différent, et
// doit donc afficher SON compte à rebours. `grace` = { [cle]: 'AAAA-MM-JJ' }
// (users.jetonsGrace) — la date à laquelle l'élément s'est retrouvé sans jeton.
// Un élément qui retrouve un jeton sort de la map : le compteur repart à neuf
// s'il est écarté de nouveau.
export function majGrace(ecartes = [], grace = {}, aujourdhui) {
  const out = {};
  for (const cle of ecartes) out[cle] = grace?.[cle] || aujourdhui;
  return out;
}
// État d'affichage d'un élément écarté : combien de jours il lui reste, et
// s'il est déjà inaccessible.
export function etatEcarte(cle, grace = {}, aujourdhui) {
  const depuis = grace?.[cle];
  return {
    depuis: depuis || null,
    jours: joursDeGraceRestants(depuis, aujourdhui),
    expire: graceExpiree(depuis, aujourdhui),
  };
}

// ── Fenêtre mensuelle ─────────────────────────────────────────────
// Un seul changement par MOIS CALENDAIRE : le droit se recharge le 1er.
// `moisCourant` est passé par l'appelant (« AAAA-MM ») — pas de Date.now()
// implicite ici, pour rester pur et testable.
export const moisDe = (iso) => String(iso || '').slice(0, 7);
export function peutChanger(user, moisCourant) {
  if (!moisCourant) return true;
  const dernier = moisDe(user?.jetonsMajLe);
  return dernier !== moisCourant;
}
// Ces mouvements ne consomment PAS le changement du mois : reprendre un don
// (sinon une délégation ratée bloque un accès pendant un mois) et promouvoir
// un jeton 👁 en ⚙️ (manipulation purement technique).
export const MOUVEMENTS_LIBRES = ['reprise-don', 'promotion'];
export const changementLibre = (motif) => MOUVEMENTS_LIBRES.includes(motif);
